// Dashboard shell — KPI tiles + persistent ask bar + refinement classifier.
// Each tile owns its own streaming state via useTileStream so the global
// chat-state machine isn't bottlenecked by 6 parallel queries on mount.
// The `submit` prop from ShellProps still works for "append a new tile";
// the persistent ask bar additionally handles "modify last tile" and
// "reset" via a keyword-based intent classifier.
import { useCallback, useState } from 'react';
import { Tile } from './dashboard/Tile';
import { RefinementHistory } from './dashboard/RefinementHistory';
import { useTileStream } from './dashboard/useTileStream';
import { OVERVIEW_QUERIES, classifyIntent } from './dashboard/overview-queries';
import type { OverviewQuery } from './dashboard/overview-queries';
import { PresentationContainer } from '../components/presentation/PresentationContainer';
import type { ShellProps } from './types';

const SCOPES = ['National', 'TX', 'VA', 'FL'] as const;
type Scope = typeof SCOPES[number];

interface AddedTile {
  id: string;
  query: string;
  refinedTag?: string;
}

function scopedQuery(base: string, scope: Scope): string {
  if (scope === 'National') return base;
  // Soft prefix — the LLM will pick up the constraint without us having to
  // mutate the question semantically.
  return `${base} (limit to ${scope})`;
}

export function DashboardShell({ submit: _unusedSubmit }: ShellProps) {
  const [scope, setScope] = useState<Scope>('National');
  const [added, setAdded] = useState<AddedTile[]>([]);
  const [askValue, setAskValue] = useState('');
  const [history, setHistory] = useState<string[]>(['National scope']);
  const [focusedTile, setFocusedTile] = useState<string | null>(null);
  const [resetCounter, setResetCounter] = useState(0); // incrementing key forces tile remount on reset

  const onAsk = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    const text = askValue.trim();
    if (!text) return;
    const intent = classifyIntent(text);
    if (intent === 'reset') {
      setAdded([]);
      setHistory(['National scope']);
      setResetCounter((n) => n + 1);
    } else if (intent === 'modify' && focusedTile) {
      // Modify: replace the focused tile with a refined version. For an
      // *added* tile we re-set its query; for a standing tile we add a
      // refined twin alongside the original (keeps the original visible
      // for comparison).
      const id = `refined-${Date.now()}`;
      setAdded((prev) => [...prev, { id, query: text, refinedTag: text.slice(0, 24) }]);
      setHistory((prev) => [...prev, `+ ${text.slice(0, 32)}`]);
    } else {
      // Append: new tile at the end of the grid.
      const id = `added-${Date.now()}`;
      setAdded((prev) => [...prev, { id, query: text }]);
      setHistory((prev) => [...prev, `+ ${text.slice(0, 32)}`]);
    }
    setAskValue('');
  }, [askValue, focusedTile]);

  return (
    <div className="dash-root">
      <div className="dash-topbar">
        <div className="brand brand--compact">
          <div className="brand-mark">HDW</div>
          <div>
            <div className="brand-title">Unified Search</div>
            <div className="brand-subtitle">Public dashboard — no sign-in</div>
          </div>
        </div>
        <div className="dash-scope-row" role="group" aria-label="Scope filter">
          <span className="dash-scope-label">SCOPE</span>
          {SCOPES.map((s) => (
            <button
              key={s}
              className={`dash-scope-btn ${s === scope ? 'dash-scope-btn--active' : ''}`}
              onClick={() => { setScope(s); setHistory((prev) => [...prev, s === 'National' ? 'National scope' : `Scope: ${s}`]); }}
              aria-pressed={s === scope}
            >
              {s}
            </button>
          ))}
          <span className="dash-scope-fy">FY 2025</span>
        </div>
      </div>

      <form className="dash-askbar" onSubmit={onAsk}>
        <div className="dash-askbar__left">
          <span className="dash-askbar__kicker">ASK ABOUT THIS DASHBOARD</span>
          <div className="dash-askbar__input-row">
            <span className="dash-askbar__icon" aria-hidden="true">⌕</span>
            <input
              className="dash-askbar__input"
              value={askValue}
              onChange={(e) => setAskValue(e.target.value)}
              placeholder='Try: "limit to rural counties" or "show grants by discipline"'
            />
            <button type="submit" className="dash-askbar__submit" disabled={!askValue.trim()}>Refine ↵</button>
          </div>
        </div>
        <div className="dash-askbar__chips">
          <span className="dash-askbar__chip-label">RECENT</span>
          {history.slice(-3).map((h, i) => (
            <span
              key={`${h}-${i}`}
              className={i === 2 ? 'dash-askbar__chip--active' : 'dash-askbar__chip'}
            >
              {h}
            </span>
          ))}
        </div>
      </form>

      <div className="dash-grid">
        {OVERVIEW_QUERIES.map((q) => (
          <StandingTile
            key={`${q.id}-${resetCounter}`}
            spec={q}
            scope={scope}
            onFocus={() => setFocusedTile(q.id)}
          />
        ))}
        {added.map((t) => (
          <AddedTileEl
            key={t.id}
            tile={t}
            scope={scope}
            onFocus={() => setFocusedTile(t.id)}
          />
        ))}
      </div>

      <RefinementHistory steps={history} onReset={() => {
        setAdded([]);
        setHistory(['National scope']);
        setScope('National');
        setResetCounter((n) => n + 1);
      }} />
    </div>
  );
}

interface StandingTileProps {
  spec: OverviewQuery;
  scope: Scope;
  onFocus: () => void;
}

function StandingTile({ spec, scope, onFocus }: StandingTileProps) {
  const tile = useTileStream(scopedQuery(spec.query, scope), spec.id);
  return (
    <div onMouseEnter={onFocus} style={{ display: 'contents' }}>
      <Tile
        span={spec.span}
        title={spec.title}
        subtitle={spec.subtitle}
        presPill={tile.streaming ? '…' : tile.assistant.presentation?.type}
        sourceCount={tile.assistant.citations.length}
        loading={tile.streaming && !tile.assistant.presentation}
      >
        {tile.assistant.error ? (
          <div className="assistant-error">⚠ {tile.assistant.error}</div>
        ) : tile.streaming && !tile.assistant.presentation ? (
          <div className="tool-use-indicator" aria-live="polite">
            <span className="spinner" aria-hidden="true">✦</span>
            <span>Looking up</span>
          </div>
        ) : tile.assistant.presentation ? (
          <PresentationContainer p={tile.assistant.presentation} />
        ) : (
          <div className="info-panel-muted">No data yet.</div>
        )}
      </Tile>
    </div>
  );
}

interface AddedTileProps {
  tile: AddedTile;
  scope: Scope;
  onFocus: () => void;
}

function AddedTileEl({ tile, scope, onFocus }: AddedTileProps) {
  const stream = useTileStream(scopedQuery(tile.query, scope), null, tile.refinedTag);
  return (
    <div onMouseEnter={onFocus} style={{ display: 'contents' }}>
      <Tile
        span="6"
        title={tile.query.length > 80 ? tile.query.slice(0, 77) + '…' : tile.query}
        presPill={stream.streaming ? '…' : stream.assistant.presentation?.type}
        sourceCount={stream.assistant.citations.length}
        refinedTag={tile.refinedTag}
        loading={stream.streaming && !stream.assistant.presentation}
        highlighted
      >
        {stream.assistant.error ? (
          <div className="assistant-error">⚠ {stream.assistant.error}</div>
        ) : stream.streaming && !stream.assistant.presentation ? (
          <div className="tool-use-indicator" aria-live="polite">
            <span className="spinner" aria-hidden="true">✦</span>
            <span>Looking up</span>
          </div>
        ) : stream.assistant.presentation ? (
          <PresentationContainer p={stream.assistant.presentation} />
        ) : (
          <div className="info-panel-muted">No data yet.</div>
        )}
      </Tile>
    </div>
  );
}
